import { Button } from '@emailforge/ui';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Icon } from '@/components/ui/icons';
import { Logo } from '@/components/ui/Logo';
import { setSession } from '@/features/auth/authSlice';
import { identify } from '@/lib/monitoring';
import { useAppDispatch } from '@/store/hooks';

type State = 'working' | 'ok' | 'error';

/**
 * Landing for the magic-link URL embedded in the sign-in email.
 * Path: /auth/verify?token=...
 *
 * Posts the token to /api/auth/magic-link/verify, hydrates the session,
 * applies tenant branding, and routes into the app.
 */
export function VerifyMagicLink() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [state, setState] = useState<State>('working');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    const token = params.get('token');
    if (!token) {
      setState('error');
      setMessage('Missing token in URL');
      return;
    }

    (async () => {
      try {
        const verifyRes = await fetch('/api/auth/magic-link/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        if (!verifyRes.ok) {
          const body = (await verifyRes.json().catch(() => ({}))) as { message?: string };
          throw new Error(body.message ?? `Verification failed (${verifyRes.status})`);
        }
        const { accessToken, refreshToken } = (await verifyRes.json()) as {
          accessToken: string;
          refreshToken?: string;
        };

        const meRes = await fetch('/api/auth/me', {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!meRes.ok) throw new Error('Could not hydrate session');
        const me = await meRes.json();

        dispatch(setSession({ me, accessToken, refreshToken }));
        identify(me.user.id, { email: me.user.email });
        const branding = me.memberships?.[0]?.branding;
        if (branding) {
          const { applyBranding } = await import('@/features/branding/applyBranding');
          applyBranding(branding);
        }

        setState('ok');
        // tiny delay so the success state is visible
        setTimeout(() => navigate('/app', { replace: true }), 500);
      } catch (err) {
        setState('error');
        setMessage((err as Error).message);
      }
    })();
  }, [params, dispatch, navigate]);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--paper-2)',
      }}
    >
      <div
        className="mf-card"
        style={{ maxWidth: 420, padding: 40, textAlign: 'center', boxShadow: 'var(--sh-lg)' }}
      >
        <Logo size={36} />

        {state === 'working' && (
          <>
            <h1 className="display" style={{ fontSize: 24, margin: '18px 0 8px' }}>
              Signing you in…
            </h1>
            <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              Verifying your magic link.
            </div>
          </>
        )}

        {state === 'ok' && (
          <>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: 'var(--spark-soft)',
                color: 'var(--spark)',
                border: '1px solid var(--spark-line)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '20px auto 12px',
              }}
            >
              <Icon.check size={20} />
            </div>
            <h1 className="display" style={{ fontSize: 24, margin: '0 0 8px' }}>
              You're in
            </h1>
            <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              Redirecting to your workspace…
            </div>
          </>
        )}

        {state === 'error' && (
          <>
            <h1 className="display" style={{ fontSize: 22, margin: '18px 0 8px' }}>
              Link didn't work
            </h1>
            <p style={{ fontSize: 13, color: 'var(--err)', margin: '0 0 18px' }}>{message}</p>
            <Button variant="flame" onClick={() => navigate('/login')}>
              Back to sign in
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
